import { IconProps } from '@euler/app/components/icons/types';
import { CompactListIcon } from '@euler/app/tv/components/dashboard/icons/CompactListIcon';
import { GridIcon } from '@euler/app/tv/components/dashboard/icons/GridIcon';
import { HorizontalListIcon } from '@euler/app/tv/components/dashboard/icons/HorizontalListIcon';
import { kDashboardToolbarHeight } from '@euler/app/tv/functions/constants';
import { TaskListMode } from '@euler/app/tv/functions/useDashboardTaskListMetrics';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { PresentationTheme } from '@euler/app/tv/theme/types';
import { Center } from '@euler/components/Center';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InspectionTaskStatus } from '@euler/model/enum';
import { TaskListSearchParams } from '@euler/services';
import { LinearGradient } from 'expo-linear-gradient';
import { FC, memo, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  LayoutChangeEvent,
  LayoutRectangle,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export type TaskListTabType =
  | 'default'
  | 'inspection-finished'
  | 'construction-finished'
  | 'finished'
  | 'search'
  | 'bookmarked'
  | 'visited';

export type TaskListTabInfo = {
  type: TaskListTabType;
  text: string;
  toSearchParams: () => TaskListSearchParams;
};

export type TaskListModeInfo = {
  mode: TaskListMode;
  Icon: FC<IconProps>;
  size?: number;
};

export const TaskListModes: TaskListModeInfo[] = [
  {
    mode: 'horizontal-complete',
    Icon: HorizontalListIcon,
  },
  {
    mode: 'vertical-complete',
    Icon: GridIcon,
    size: 22,
  },
  {
    mode: 'vertical-compact',
    Icon: CompactListIcon,
  },
];

export const DashboardTaskListTabItems: TaskListTabInfo[] = [
  {
    type: 'default',
    text: '在厂车辆',
    toSearchParams: () => ({
      status: [InspectionTaskStatus.InProgress, InspectionTaskStatus.Pending],
    }),
  },
  {
    type: 'inspection-finished',
    text: '检测完成',
    toSearchParams: () => ({
      status: InspectionTaskStatus.InProgress,
      childStatus: 'inspected',
    }),
  },
  {
    type: 'construction-finished',
    text: '施工完成',
    toSearchParams: () => ({
      status: InspectionTaskStatus.InProgress,
      childStatus: 'constructed',
    }),
  },
];

export const HistoryTaskListTabItems: TaskListTabInfo[] = [
  {
    type: 'finished',
    text: '已完工车辆',
    toSearchParams: () => ({
      status: [InspectionTaskStatus.Finished],
    }),
  },
];

export const BookmarkTaskListTabItems: TaskListTabInfo[] = [
  {
    type: 'visited',
    text: '最近查看的车辆',
    toSearchParams: () => ({}),
  },
  {
    type: 'bookmarked',
    text: '收藏的车辆',
    toSearchParams: () => ({}),
  },
];

export const TaskListToolbar = memo(
  ({
    tabs = DashboardTaskListTabItems,
    activeTabType,
    activeListMode,
    style,
    loading,
    onTabChanged,
    onModeChanged,
  }: {
    tabs?: TaskListTabInfo[];
    activeTabType: TaskListTabType;
    activeListMode: TaskListMode;
    style?: StyleProp<ViewStyle>;
    loading?: boolean;
    onTabChanged?: (type: TaskListTabType) => void;
    onModeChanged?: (mode: TaskListMode) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const offset = useSharedValue(0);

    const onTabPress = useCallback(
      (type: TaskListTabType, layout: LayoutRectangle) => {
        if (activeTabType !== type) {
          onTabChanged?.(type);
          offset.value = layout.x;
        }
      },
      [activeTabType, offset, onTabChanged],
    );

    const onModePress = useCallback(
      (mode: TaskListMode) => {
        if (activeListMode !== mode) {
          onModeChanged?.(mode);
        }
      },
      [activeListMode, onModeChanged],
    );

    const animatedStyle = useAnimatedStyle(() => {
      return {
        transform: [
          {
            translateX: withTiming(offset.value, { duration: 200 }),
          },
        ],
      };
    });

    return (
      <View style={style}>
        <HStack
          css={`
            height: ${rpx(kDashboardToolbarHeight)}px;
          `}
        >
          {loading ? (
            <Center style={StyleSheet.absoluteFill}>
              <ActivityIndicator />
            </Center>
          ) : null}
          <Animated.View
            style={animatedStyle}
            css={`
              width: ${rpx(96)}px;
              height: ${rpx(22)}px;
              border-radius: ${rpx(22) / 2}px;
              overflow: hidden;
              position: absolute;
              left: 0;
              bottom: 0;
            `}
          >
            <LinearGradient
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              colors={[
                theme.dashboard.tab.activeEffect.startColor,
                theme.dashboard.tab.activeEffect.endColor,
              ]}
              css={`
                flex: 1;
              `}
            />
          </Animated.View>
          <HStack justifyContent="flex-start">
            {tabs.map(item => (
              <ListTabCell
                key={item.type}
                item={item}
                active={activeTabType === item.type}
                theme={theme}
                onPress={onTabPress}
                css={`
                  margin-right: ${rpx(89)}px;
                `}
              />
            ))}
          </HStack>
          {onModeChanged != null && (
            <HStack justifyContent="flex-end">
              {TaskListModes.map(item => (
                <ListModeCell
                  key={item.mode}
                  item={item}
                  active={activeListMode === item.mode}
                  theme={theme}
                  onPress={onModePress}
                  css={`
                    margin-left: ${rpx(15)}px;
                  `}
                />
              ))}
            </HStack>
          )}
        </HStack>
      </View>
    );
  },
);

const ListTabCell = memo(
  ({
    item,
    active,
    theme,
    style,
    onPress,
  }: {
    item: TaskListTabInfo;
    active?: boolean;
    theme: PresentationTheme;
    style?: StyleProp<ViewStyle>;
    onPress?: (type: TaskListTabType, layout: LayoutRectangle) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [layout, setLayout] = useState<LayoutRectangle>();

    const handlePress = useCallback(() => {
      if (layout == null) return;
      onPress?.(item.type, layout);
    }, [item.type, layout, onPress]);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
      setLayout(e.nativeEvent.layout);
    }, []);

    return (
      <TouchableOpacity
        disabled={layout == null}
        onPress={handlePress}
        hitSlop={PredefinedHitSlops.large}
        css={`
          height: ${rpx(36)}px;
          align-items: center;
          justify-content: center;
        `}
        style={style}
        onLayout={onLayout}
      >
        <Text
          css={`
            font-family: ${active
              ? FontFamily.NotoSans.Bold
              : FontFamily.NotoSans.Regular};
            font-size: ${rpx(24)}px;
            line-height: ${rpx(36)}px;
            color: ${active
              ? theme.dashboard.tab.activeTextColor
              : theme.dashboard.tab.textColor};
          `}
        >
          {item.text}
        </Text>
      </TouchableOpacity>
    );
  },
);

const ListModeCell = memo(
  ({
    item,
    active,
    theme,
    style,
    onPress,
  }: {
    item: TaskListModeInfo;
    active?: boolean;
    theme: PresentationTheme;
    style?: StyleProp<ViewStyle>;
    onPress?: (type: TaskListMode) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const Icon = item.Icon;
    const handlePress = useCallback(() => {
      onPress?.(item.mode);
    }, [item.mode, onPress]);

    return (
      <TouchableOpacity
        onPress={handlePress}
        hitSlop={PredefinedHitSlops.small}
        css={`
          height: ${rpx(36)}px;
          align-items: center;
          justify-content: center;
        `}
        style={style}
      >
        <Icon
          color={
            active
              ? theme.dashboard.mode.activeIconColor
              : theme.dashboard.mode.iconColor
          }
          size={rpx(item.size ?? 24)}
        />
      </TouchableOpacity>
    );
  },
);
